import React, { useState } from "react"
import { HStack, Text, VStack, Link as TriggerLink } from "@chakra-ui/react"
import { Link } from "@hydrologiq/shared/components/Link"
import Popover from "@hydrologiq/shared/chakra/Popover"
import * as gqlTypes from "@hydrologiq/gql-experience/graphql"

interface CurrentOrder {
  id: string
  name: string
  status: gqlTypes.OrderStatus
  start: string
  end: string
  quantity?: number | null
  quantity_consumed?: number | null
  customer?: { id: string; name: string } | null
  site?: { id: string; name: string } | null
}

interface MultiCurrentOrdersDisplayProps {
  orders: CurrentOrder[]
  maxVisible?: number
  showAdditionalInfo?: boolean
}

/**
 * Display multiple current orders with a "show more" popover for overflow.
 * Shows first N orders inline with format "Order name (Customer site)", with remaining orders in a popover.
 */
const MultiCurrentOrdersDisplay: React.FC<MultiCurrentOrdersDisplayProps> = ({
  orders,
  maxVisible = 1,
  showAdditionalInfo = false,
}) => {
  const [isOpen, setIsOpen] = useState(false)

  if (!orders || orders.length === 0) {
    return <Text>No order</Text>
  }

  // Format order display text: "Order name (Customer site)"
  const formatOrderText = (order: CurrentOrder) => {
    if (!showAdditionalInfo) {
      return order.name
    }
    const customerName = order.customer?.name || "Unknown customer"
    const siteName = order.site?.name || "Unknown site"
    return `${order.name} (${customerName} ${siteName})`
  }

  // Single order - display directly
  if (orders.length === 1) {
    const order = orders[0]
    return (
      <Link href={`/orders`} variant="primary" truncate maxWidth="100%">
        {formatOrderText(order)}
      </Link>
    )
  }

  // Multiple orders - show first N + popover for rest
  const visibleOrders = orders.slice(0, maxVisible)
  const remainingOrders = orders.slice(maxVisible)
  const remainingCount = remainingOrders.length

  return (
    <HStack gap={1} flexWrap="nowrap" overflow="hidden" maxWidth="100%">
      {visibleOrders.map((order) => (
        <Link key={order.id} href={`/orders`} variant="primary" truncate maxWidth="100%">
          {formatOrderText(order)}
        </Link>
      ))}
      {remainingCount > 0 && (
        <Popover
          placement="bottom"
          open={isOpen}
          onOpenChange={setIsOpen}
          additionalContent={<TriggerLink variant="secondary">+{remainingCount} more</TriggerLink>}
        >
          <VStack align="stretch" gap={2} maxH="300px" overflowY="auto" p={4}>
            {remainingOrders.map((order) => (
              <Link key={order.id} href={`/orders`} variant="primary">
                {formatOrderText(order)}
              </Link>
            ))}
          </VStack>
        </Popover>
      )}
    </HStack>
  )
}

export default MultiCurrentOrdersDisplay
